import { forwardRef, createContext, useContext, useMemo, useState, useEffect } from 'react';
import type { PropsWithoutRef, ReactNode, ReactElement, RefObject } from 'react';
import styled, { css } from 'styled-components';
import { createPortal } from 'react-dom';

import type { RefElement, TestIdProp, WithAttributes } from '../../types';
import { defaultThemeProp } from '../../theme';
import {
  useConfiguration,
  useConsolidatedRef,
  useElement,
  useFocusTrap,
  usePopoverMap,
  useScrollToggle,
  useTestIds
} from '../../hooks';
import Configuration from '../Configuration';
import { withTestIds } from '../../utils';

import { getFullscreenTestIds } from './Fullscreen.test-ids';

export type FullscreenProps = WithAttributes<
  'div',
  {
    children: ReactNode;
  }
> &
  TestIdProp;

export const StyledFullscreen = styled.div<{ fullscreen?: boolean }>(({ theme, fullscreen }) => {
  return css`
    ${fullscreen &&
    css`
      --content-height-in-view: calc(
        100vh - var(--appshell-top-offset, 0rem) - var(--appshell-hard-bottom-offset, 0rem)
      );
      position: fixed;
      z-index: ${theme.base['z-index'].backdrop};
      inset-inline: 0;
      inset-block-start: var(--appshell-top-offset, 0);
      inset-block-end: var(--appshell-hard-bottom-offset, 0);
      background: ${theme.base.palette['primary-background']};
    `}
  `;
});

StyledFullscreen.defaultProps = defaultThemeProp;

export type FullscreenContextValue = [boolean | undefined, () => void] | undefined;

export const FullscreenContext = createContext<FullscreenContextValue>(undefined);

export const useFullscreenContext = () => {
  // Ensure easier/safer destructuring in use
  return useContext(FullscreenContext);
};

const PortalRender = ({
  parentJSX,
  parentElRef
}: {
  parentJSX: ReactElement;
  parentElRef: RefObject<HTMLDivElement>;
}) => {
  const { portalTarget } = useConfiguration();
  useFocusTrap(parentElRef);
  const { disableScroll, enableScroll } = useScrollToggle();

  useEffect(() => {
    disableScroll();
    return enableScroll;
  }, []);

  return portalTarget
    ? createPortal(
        <Configuration portalTarget={parentElRef.current ?? undefined}>{parentJSX}</Configuration>,
        portalTarget
      )
    : null;
};

export default withTestIds(
  forwardRef<RefElement<FullscreenProps>, PropsWithoutRef<FullscreenProps>>(function Fullscreen(
    { children, testId, ...restProps },
    ref
  ) {
    const testIds = useTestIds(testId, getFullscreenTestIds);
    // setParentEl ensures one extra render needed for keeping div passed to PortalRender up to date
    const [parentEl, setParentEl] = useElement<HTMLDivElement>();
    const parentElRef = useConsolidatedRef(setParentEl, ref);

    const [targetEl, setTargetEl] = useElement<HTMLDivElement>();

    const [fullscreen, setFullscreen] = useState<boolean | undefined>(undefined);

    const ctxValue: FullscreenContextValue = useMemo(() => {
      return [
        fullscreen,
        () => {
          setFullscreen(cur => !cur);
        }
      ];
    }, [fullscreen]);

    const popoverId = usePopoverMap(targetEl, !!fullscreen);

    const parentJSX = (
      <StyledFullscreen
        data-testid={testIds.root}
        data-fullscreen-enabled={fullscreen ? true : undefined}
        {...restProps}
        ref={parentElRef}
        fullscreen={!!fullscreen}
        data-popover-id={popoverId}
      >
        {children}
      </StyledFullscreen>
    );

    return (
      <FullscreenContext.Provider value={ctxValue}>
        {fullscreen && parentEl ? (
          <>
            {/* DOM element required for outer event management. */}
            <div ref={setTargetEl} style={{ display: 'none' }} />
            <PortalRender parentJSX={parentJSX} parentElRef={parentElRef} />
          </>
        ) : (
          parentJSX
        )}
      </FullscreenContext.Provider>
    );
  }),
  getFullscreenTestIds
);
